import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export interface MerchantOperatingHoursDay {
  dayOfWeek: number;
  dayLabel: string;
  enabled: boolean;
  hours: string;
}

export interface MerchantOperatingHours {
  source: string;
  isConfigured?: boolean;
  manualOpen: boolean;
  withinHours: boolean | null;
  days: MerchantOperatingHoursDay[];
  summary: string;
}

export interface MerchantBankAccount {
  bankName: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  bankCode: string | null;
  paymentNote: string | null;
  hasBankDetails: boolean;
}

export interface MerchantRecentActivity {
  id: string;
  type: string;
  orderId?: number;
  orderNumber?: string;
  status?: string;
  statusLabel?: string;
  total?: number;
  timestamp: string;
  description?: string;
}

export interface MerchantDetailOnboarding {
  status?: string;
  packagePaymentStatus?: string;
  isKycApproved?: boolean;
  isPackagePaymentSatisfied?: boolean;
  isMarketplaceLive?: boolean;
}

export interface MerchantDetailData {
  id: number;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  is_active?: boolean;
  created_at: string;
  restaurantId?: number;
  restaurantName?: string;
  restaurantAddress?: string;
  latitude?: number | null;
  longitude?: number | null;
  restaurant_image?: string;
  creditBalance?: number | null;
  operatingHours?: MerchantOperatingHours | null;
  bankAccount?: MerchantBankAccount | null;
  recentActivity?: MerchantRecentActivity[];
  categoryName?: string | null;
  taxId?: string | null;
  storeManualOpen?: boolean | null;
  restaurantStatus?: string | null;
  isListed?: boolean;
  kycStatus?: string | null;
  packagePaymentStatus?: string | null;
  isKycApproved?: boolean;
  isPackagePaymentSatisfied?: boolean;
  isMarketplaceLive?: boolean;
  onboarding?: MerchantDetailOnboarding | null;
}

interface MerchantDetailResponse {
  success: boolean;
  data: MerchantDetailData & Record<string, unknown>;
}

function normalizeOnboarding(
  raw: MerchantDetailData & Record<string, unknown>
): MerchantDetailOnboarding | null {
  const nested =
    (raw.onboarding as MerchantDetailOnboarding | undefined) ??
    (raw.onboarding_summary as MerchantDetailOnboarding | undefined);
  if (nested && typeof nested === 'object') {
    return {
      status: nested.status ?? (nested as Record<string, string>).status,
      packagePaymentStatus:
        nested.packagePaymentStatus ??
        (nested as Record<string, string>).package_payment_status,
      isKycApproved:
        nested.isKycApproved ??
        (nested as Record<string, boolean>).is_kyc_approved,
      isPackagePaymentSatisfied:
        nested.isPackagePaymentSatisfied ??
        (nested as Record<string, boolean>).is_package_payment_satisfied,
      isMarketplaceLive:
        nested.isMarketplaceLive ??
        (nested as Record<string, boolean>).is_marketplace_live,
    };
  }
  return null;
}

function normalizeMerchantDetail(
  raw: MerchantDetailData & Record<string, unknown>
): MerchantDetailData {
  const operatingHours =
    raw.operatingHours ??
    (raw.operating_hours as MerchantDetailData['operatingHours']) ??
    null;
  const bankAccount =
    raw.bankAccount ??
    (raw.bank_account as MerchantDetailData['bankAccount']) ??
    null;
  const recentActivity =
    raw.recentActivity ??
    (raw.recent_activity as MerchantDetailData['recentActivity']) ??
    [];
  const restaurantId =
    raw.restaurantId ?? (raw.restaurant_id as number | undefined) ?? undefined;
  const onboarding = normalizeOnboarding(raw);
  const kycStatus =
    (raw.kycStatus as string | undefined) ??
    (raw.kyc_status as string | undefined) ??
    onboarding?.status ??
    null;
  const packagePaymentStatus =
    (raw.packagePaymentStatus as string | undefined) ??
    (raw.package_payment_status as string | undefined) ??
    onboarding?.packagePaymentStatus ??
    null;

  return {
    ...raw,
    restaurantId,
    operatingHours,
    bankAccount,
    recentActivity,
    isListed: raw.isListed ?? (raw.is_listed as boolean | undefined),
    kycStatus,
    packagePaymentStatus,
    isKycApproved:
      raw.isKycApproved ??
      (raw.is_kyc_approved as boolean | undefined) ??
      onboarding?.isKycApproved,
    isPackagePaymentSatisfied:
      raw.isPackagePaymentSatisfied ??
      (raw.is_package_payment_satisfied as boolean | undefined) ??
      onboarding?.isPackagePaymentSatisfied,
    isMarketplaceLive:
      raw.isMarketplaceLive ??
      (raw.is_marketplace_live as boolean | undefined) ??
      onboarding?.isMarketplaceLive,
    onboarding,
    categoryName:
      raw.categoryName ?? (raw.category_name as string | null) ?? null,
    taxId: raw.taxId ?? (raw.tax_id as string | null) ?? null,
    restaurantName:
      raw.restaurantName ?? (raw.restaurant_name as string | undefined),
    restaurantAddress:
      raw.restaurantAddress ?? (raw.restaurant_address as string | undefined),
  };
}

export function useMerchantDetail(id: string | undefined) {
  const [data, setData] = useState<MerchantDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMerchant = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<MerchantDetailResponse>(`/merchants/${id}`);
      if (res.data.success && res.data.data) {
        setData(normalizeMerchantDetail(res.data.data));
      }
    } catch (e: unknown) {
      const err = e as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      setError(
        err.response?.data?.message || err.message || 'Failed to load merchant'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMerchant();
  }, [fetchMerchant]);

  return { data, loading, error, refetch: fetchMerchant };
}
